/**
 * sportRoutes.js — Single source of truth for sport routing and team config.
 *
 * Defines which routes each sport gets (common + overrides), team switching
 * config, and the AskAI component key per sport. App.js maps these definitions
 * to actual React elements via a component registry.
 */

// ═══════════════════════════════════════════════════════════════════════════
// SPORTS LIST & TEAM CONFIG
// ═══════════════════════════════════════════════════════════════════════════

export const SPORTS = ['basketball', 'baseball', 'golf', 'soccer', 'hockey', 'football', 'f1'];

export const TEAM_CONFIGS = {
  basketball: { defaultTeamId: 'slamball', sportFlag: 'isBasketball' },
  baseball:   { defaultTeamId: 'slam',     sportFlag: 'isBaseball', validTeamIds: ['slam', 'yankees'] },
  golf:       { defaultTeamId: 'slam',     sportFlag: 'isGolf' },
  soccer:     { defaultTeamId: 'slam',     sportFlag: 'isSoccer' },
  hockey:     { defaultTeamId: 'slam',     sportFlag: 'isHockey' },
  football:   { defaultTeamId: 'slam',     sportFlag: 'isFootball' },
  f1:         { defaultTeamId: 'slam',     sportFlag: 'isF1' },
};

// ═══════════════════════════════════════════════════════════════════════════
// ROUTE DEFINITIONS
// Each entry: { path, componentKey, props?, suspense?, lazy? }
// componentKey maps to a component in App.js COMPONENT_MAP
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Routes shared by ALL sports. GM Dashboard, S&C, video, scouting, etc.
 */
export const COMMON_ROUTES = [
  // Agent Builder (moved to GM Dashboard 'agents' tab for baseball; standalone for other sports)
  { path: 'canvas', componentKey: 'CoachingCanvas' },
  { path: 'locker-room', componentKey: 'LockerRoom', suspense: true },

  // GM Dashboard (ported to ALL sports)
  { path: 'gm', componentKey: 'GMDashboard' },
  { path: 'gm/cap-sheet', componentKey: 'GMDashboard', props: { initialTab: 'roster' } },
  { path: 'gm/portal', componentKey: 'GMDashboard', props: { initialTab: 'portal' } },
  { path: 'gm/compliance', componentKey: 'GMDashboard', props: { initialTab: 'compliance' } },
  { path: 'gm/player/:playerName', componentKey: 'PlayerDetail' },

  // Player Development — Video Hub + legacy route aliases
  { path: 'video', componentKey: 'VideoHubPage' },
  { path: 'video-analytics', componentKey: 'VideoAnalyticsPage' },
  { path: 'drop-zone', componentKey: 'DropZonePage' },
  { path: 'video-analyzer', componentKey: 'VideoAnalyticsPage' },
  { path: 'my-library', componentKey: 'MyLibraryPage' },
  { path: 'mobile-app', componentKey: 'MobileAppEmulatorPage' },
  { path: 'practice-film', componentKey: 'SlamMobile' },
  { path: 'development', componentKey: 'PlayerDevelopment' },
  { path: 'health-integrations', componentKey: 'HealthIntegrations' },

  // Roster
  { path: 'player/:playerId', componentKey: 'TeamPlayerProfile' },

  // Scouting
  { path: 'ask-slam-ai', componentKey: '_ASK_AI_' }, // resolved per-sport
  { path: 'scouting', componentKey: 'Scouting' },

  // Player Acquisition
  { path: 'transfer-portal', componentKey: 'Scouting' },

  // Load Management & Biometrics
  { path: 'load-management', componentKey: 'CatapultDashboard' },
  { path: 'biometrics', componentKey: 'BiometricsPage' },

  // Strength & Conditioning
  { path: 'workout-generator', componentKey: 'WorkoutGeneratorPage' },
  { path: 'nutrition', componentKey: 'NutritionDiaryPage' },
  { path: 'ai-coach', componentKey: 'AICoachPage' },
  { path: 'journal', componentKey: 'SmartJournalPage' },
  { path: 'publish', componentKey: 'PublishCreatorPage' },
  { path: 'strength-admin', componentKey: 'StrengthAdminPage' },
  { path: 'crossfit-generator', componentKey: 'CrossFitGeneratorPage' },
  { path: 'workout-history', componentKey: 'WorkoutHistoryPage' },
  { path: 'movement-analysis', redirect: 'advanced-scouting' }, // merged into Advanced Scouting

  // System
  { path: 'reports', redirect: 'gm' }, // BI reports moved to GM Dashboard Settings tab
  { path: 'integrations', componentKey: 'SystemIntegrations' },
  { path: 'settings', componentKey: 'Settings' },
  { path: 'user-management', componentKey: 'Permissions' },

  // Legacy redirects (common across sports)
  { path: 'agent-universe', redirect: 'canvas' },
  { path: 'coaching-canvas', redirect: 'canvas' },
  { path: 'catapult', redirect: 'load-management' },
];

/**
 * Sport-specific AskAI component keys.
 * The _ASK_AI_ placeholder in COMMON_ROUTES gets resolved to these.
 */
export const SPORT_ASK_AI = {
  basketball: 'BasketballAskAI',
  baseball: 'BaseballAskAI',
  golf: 'GolfAskAI',
  soccer: 'SoccerAskAI',
  hockey: 'HockeyAskAI',
  football: 'FootballAskAI',
  f1: 'F1AskAI',
};

/**
 * Sport-specific route ADDITIONS (on top of COMMON_ROUTES).
 * These are routes unique to a particular sport.
 */
export const SPORT_ADDITIONS = {
  basketball: [
    { path: 'roster', componentKey: 'BasketballRosterManagement' },
    { path: 'player/:playerId', componentKey: 'BasketballPlayerPage' },
    { path: 'dashboard', componentKey: 'BasketballDashboard', readinessGate: true },
    { path: 'analytics', componentKey: 'BasketballAnalytics' },
    { path: 'bi-v2', componentKey: 'BasketballIntelligenceV2' },
    { path: 'game-film', componentKey: 'BasketballWireReport' },
    { path: 'drop-zone', componentKey: 'DropZonePage' },
    { path: 'brown-wire', componentKey: 'BrownWireReport' },
    { path: 'brown-harvard', componentKey: 'BrownHarvardDashboard' },
    { path: 'florida-houston', componentKey: 'FloridaHoustonDashboard' },
    { path: 'real-madrid', componentKey: 'RealMadridDashboard' },
    { path: 'slammobile', componentKey: 'SlamMobile' },
    { path: 'woj', componentKey: 'WojChat' },
    { path: 'settings', componentKey: 'SLAMSettings' }, // basketball uses SLAMSettings
    // Baron Bros — password-gated group (handled specially in App.js)
    // Legacy basketball redirects
    { path: 'roster-management', redirect: 'roster' },
    { path: 'player/list', redirect: 'roster' },
    { path: 'shooting-analysis', redirect: 'bi-v2' },
    { path: 'performance', redirect: 'analytics' },
    { path: 'biv2', redirect: 'bi-v2' },
    { path: 'basketball-intelligence-2', redirect: 'bi-v2' },
    { path: 'video-analysis', redirect: 'game-film' },
    { path: 'wire-report', redirect: 'game-film' },
    { path: 'transfer-portal', redirect: 'gm/portal' },
    { path: 'scouting', redirect: 'analytics' },
  ],
  baseball: [
    { path: 'roster', componentKey: 'RosterWithTeamSelection', props: { sportType: 'baseball' } },
    { path: 'the-wire', componentKey: 'TheWireReport', props: { sourceId: 'tigers' } },
    { path: 'advanced-scouting', componentKey: 'PitchAnalysisDashboardPage' },
    { path: 'pitch-tipping', redirect: 'advanced-scouting' },
    { path: 'prediction-analysis/:pitcherId', componentKey: 'PitchPredictionAnalysis' },
    { path: 'statcast', componentKey: 'StatcastAgentPanel' },
    { path: 'slam-ai', redirect: 'ask-slam-ai' },
    { path: 'unc-roster', componentKey: 'UNCRosterGrid' },
    { path: 'unc-player/:playerId', componentKey: 'UNCPlayerCard' },
    { path: 'gm', componentKey: 'UNCGMDashboard' },
    { path: 'gm/cap-sheet', componentKey: 'UNCGMDashboard', props: { initialTab: 'roster' } },
    { path: 'gm/portal', componentKey: 'UNCGMDashboard', props: { initialTab: 'portal' } },
    { path: 'gm/compliance', redirect: 'gm' },
    { path: 'settings', componentKey: 'UNCGMDashboard', props: { initialTab: 'settings' } },
  ],
  golf: [
    { path: 'roster', componentKey: 'RosterWithTeamSelection', props: { sportType: 'golf' } },
    { path: 'game-film', componentKey: 'VideoAnalyticsPage' },
  ],
  soccer: [
    { path: 'roster', componentKey: 'RosterWithTeamSelection', props: { sportType: 'soccer' } },
    { path: 'game-film', componentKey: 'VideoAnalyticsPage' },
  ],
  hockey: [
    { path: 'roster', componentKey: 'RosterWithTeamSelection', props: { sportType: 'hockey' } },
    { path: 'game-film', componentKey: 'VideoAnalyticsPage' },
  ],
  football: [
    { path: 'roster', componentKey: 'RosterWithTeamSelection', props: { sportType: 'football' } },
    { path: 'game-film', componentKey: 'VideoAnalyticsPage' },
  ],
  f1: [
    { path: 'roster', componentKey: 'RosterWithTeamSelection', props: { sportType: 'f1' } },
    { path: 'game-film', componentKey: 'VideoAnalyticsPage' },
  ],
};

/**
 * Get merged routes for a sport.
 * Sport additions override common routes when paths collide.
 */
export function getRoutesForSport(sport) {
  const additions = SPORT_ADDITIONS[sport] || [];
  const askAiKey = SPORT_ASK_AI[sport] || 'BasketballAskAI';

  // Build a map of sport-specific paths for override detection
  const additionPaths = new Set(additions.map(r => r.path));

  // Start with common routes, skip any that the sport overrides
  const routes = COMMON_ROUTES
    .filter(r => !additionPaths.has(r.path))
    .map(r => {
      // Resolve the AskAI placeholder
      if (r.componentKey === '_ASK_AI_') {
        return { ...r, componentKey: askAiKey };
      }
      return r;
    });

  // Append sport-specific routes
  routes.push(...additions);

  return routes;
}
